import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";

const BedrockIdeaService = {
    listTags: async () => {
        try {
            const response = await ApiService.getData("/v1/bedrock-idea/tags");
            const tags = Array.isArray(response) ? response : response?.tags || [];
            return { success: true, data: [...tags].sort((a, b) => String(a).localeCompare(String(b))) };
        } catch (error) {
            Logger.error("Error fetching bedrock idea tags:", error);
            return { success: false, error: error.message || "Failed to fetch tags", data: [] };
        }
    },

    listIdeas: async (page = 0, pageSize = 10, sort = "createdAt:desc", tags = null, filters = {}) => {
        try {
            const params = new URLSearchParams();
            params.append("page", String(page));
            params.append("pageSize", String(pageSize));
            params.append("sort", sort);
            if (tags && Array.isArray(tags) && tags.length > 0) {
                tags
                    .map((t) => String(t).trim())
                    .filter(Boolean)
                    .forEach((t) => params.append("tags", t));
            }
            if (filters?.genHiddenWordsearch === true) {
                params.append("genHiddenWordsearch", "true");
            }
            if (filters?.genWordWheel === true) {
                params.append("genWordWheel", "true");
            }
            if (filters?.genStandaloneImage === true) {
                params.append("genStandaloneImage", "true");
            }
            if (filters?.genWordSearch9 === true) {
                params.append("genWordSearch9", "true");
            }
            if (filters?.exhausted === true || filters?.exhausted === false) {
                params.append("exhausted", filters.exhausted ? "true" : "false");
            }
            if (filters?.roundRobin === true || filters?.roundRobin === false) {
                params.append("roundRobin", filters.roundRobin ? "true" : "false");
            }
            const url = `/v1/bedrock-idea?${params.toString()}`;
            const response = await ApiService.getData(url);
            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error("Error fetching bedrock ideas:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch bedrock ideas"
            };
        }
    },

    getIdea: async (id) => {
        try {
            const response = await ApiService.getData(`/v1/bedrock-idea/${id}`);
            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error("Error fetching bedrock idea:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch bedrock idea"
            };
        }
    },

    createIdea: async (ideaData) => {
        try {
            const response = await ApiService.postData("/v1/bedrock-idea", ideaData);
            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error("Error creating bedrock idea:", error);
            return {
                success: false,
                error: error.message || "Failed to create bedrock idea"
            };
        }
    },

    updateIdea: async (id, ideaData) => {
        try {
            const response = await ApiService.putData(`/v1/bedrock-idea/${id}`, ideaData);
            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error("Error updating bedrock idea:", error);
            return {
                success: false,
                error: error.message || "Failed to update bedrock idea"
            };
        }
    },

    /** Toggle hidden message / word search generation flag on an existing idea. */
    setGenHiddenWordsearch: async (id, genHiddenWordsearch) => {
        return BedrockIdeaService.updateIdea(id, { genHiddenWordsearch: Boolean(genHiddenWordsearch) });
    },

    /** Toggle Word Wheel-only generation flag on an existing idea. */
    setGenWordWheel: async (id, genWordWheel) => {
        return BedrockIdeaService.updateIdea(id, { genWordWheel: Boolean(genWordWheel) });
    },

    /** Toggle standalone beautiful-image generation flag on an existing idea. */
    setGenStandaloneImage: async (id, genStandaloneImage) => {
        return BedrockIdeaService.updateIdea(id, { genStandaloneImage: Boolean(genStandaloneImage) });
    },

    /**
     * Sets roundRobin (Continuous) to false for every idea (all pages, no tag filter).
     * Only sends { roundRobin: false } — must not clear genStandaloneImage / other flags.
     * @returns {{ success: boolean, updated: number, errors: Array<{ id: string, error: string }> }}
     */
    resetAllContinuousOff: async () => {
        const pageSize = 100;
        let page = 0;
        let updated = 0;
        const errors = [];
        try {
            while (true) {
                const listResult = await BedrockIdeaService.listIdeas(page, pageSize, "createdAt:desc", null);
                if (!listResult.success) {
                    return {
                        success: false,
                        updated,
                        errors: [...errors, { id: null, error: listResult.error || "Failed to list ideas" }]
                    };
                }
                const content = listResult.data.content || [];
                const totalElements = listResult.data.page?.totalElements;
                const toTurnOff = content.filter((i) => i.roundRobin === true);
                for (const idea of toTurnOff) {
                    const up = await BedrockIdeaService.updateIdea(idea.id, { roundRobin: false });
                    if (up.success) {
                        updated += 1;
                    } else {
                        errors.push({ id: idea.id, error: up.error || "Update failed" });
                    }
                }
                const noMorePages =
                    content.length < pageSize ||
                    (typeof totalElements === "number" && (page + 1) * pageSize >= totalElements);
                if (noMorePages) {
                    break;
                }
                page += 1;
            }
            return { success: errors.length === 0, updated, errors };
        } catch (error) {
            Logger.error("resetAllContinuousOff:", error);
            return {
                success: false,
                updated,
                errors: [...errors, { id: null, error: error.message || "Failed to reset continuous flags" }]
            };
        }
    },

    /** Repair gen_standalone_image on ideas that already have standalone packs but lost the flag. */
    restoreStandaloneFlags: async () => {
        try {
            const response = await ApiService.postData("/v1/bedrock-idea/restore-standalone-flags", {});
            return {
                success: true,
                restored: typeof response?.restored === "number" ? response.restored : 0
            };
        } catch (error) {
            Logger.error("restoreStandaloneFlags:", error);
            return {
                success: false,
                restored: 0,
                error: error.message || "Failed to restore standalone flags"
            };
        }
    },

    deleteIdea: async (id) => {
        try {
            const response = await ApiService.deleteData(`/v1/bedrock-idea/${id}`);
            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error("Error deleting bedrock idea:", error);
            return {
                success: false,
                error: error.message || "Failed to delete bedrock idea"
            };
        }
    },

    executeIdea: async (id) => {
        try {
            const response = await ApiService.putData(`/test/bedrock-idea/execute/${id}`, {});
            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error("Error executing bedrock idea:", error);
            return {
                success: false,
                error: error.message || "Failed to execute bedrock idea"
            };
        }
    },

    /**
     * One-shot: Bedrock idea that already created puzzles for this tag → draft of that kind.
     * POST /v1/bedrock-idea/gen-draft-by-tag?tag=…&kind=crossword|grid
     */
    genDraftByTag: async (tag, kind = "crossword") => {
        try {
            const tagTrim = String(tag || "").trim();
            if (!tagTrim) {
                return { success: false, error: "Tag is required", data: null };
            }
            const params = new URLSearchParams();
            params.set("tag", tagTrim);
            if (String(kind || "").trim().toLowerCase() === "grid") {
                params.set("kind", "grid");
            }
            const response = await ApiService.postData(
                `/v1/bedrock-idea/gen-draft-by-tag?${params.toString()}`,
                {}
            );
            if (response?.code === "FAILURE") {
                return {
                    success: false,
                    error: response.message || "Gen Idea failed",
                    data: null,
                };
            }
            return { success: true, data: response };
        } catch (error) {
            Logger.error("Error generating draft by tag:", error);
            return {
                success: false,
                error: error.message || "Failed to generate draft from Bedrock idea",
                data: null,
            };
        }
    },

    // Backend keys for Bedrock Idea continuous scheduler (SystemSettingsService)
    BEDROCK_IDEA_CONTINUOUS_ENABLED: "bi_cont_enabled",
    BEDROCK_IDEA_CONTINUOUS_CRON: "bi_cont_cron",
    BEDROCK_IDEA_CONTINUOUS_ROUND_ROBIN: "bi_cont_round_robin",
    BEDROCK_IDEA_CONTINUOUS_LAST_INDEX: "bi_cont_last_index",
    BEDROCK_IDEA_CONTINUOUS_COUNT_DOWN: "bi_cont_count_down",

    getRoundRobinSchedulerConfig: async () => {
        try {
            const response = await ApiService.getData("/v1/bedrock-idea-round-robin/scheduler");
            const data = response || {};
            const k = BedrockIdeaService;
            return {
                success: true,
                data: {
                    enabled: data.enabled ?? data[k.BEDROCK_IDEA_CONTINUOUS_ENABLED] ?? false,
                    cron: data.cron ?? data[k.BEDROCK_IDEA_CONTINUOUS_CRON] ?? "0 0 0 * * ?",
                    roundRobin: data.roundRobin ?? data[k.BEDROCK_IDEA_CONTINUOUS_ROUND_ROBIN] ?? false,
                    lastIndex: data.lastIndex ?? data[k.BEDROCK_IDEA_CONTINUOUS_LAST_INDEX] ?? null,
                    countDown: data.countDown ?? data[k.BEDROCK_IDEA_CONTINUOUS_COUNT_DOWN] ?? 0
                }
            };
        } catch (error) {
            Logger.error("Error fetching round robin scheduler config:", error);
            return {
                success: false,
                error: error.message || "Failed to fetch scheduler config"
            };
        }
    },

    updateRoundRobinSchedulerConfig: async (config) => {
        try {
            const k = BedrockIdeaService;
            const countDown = config.countDown !== undefined && config.countDown !== null
                ? Number(config.countDown)
                : 0;
            const payload = {
                enabled: config.enabled,
                cron: config.cron,
                countDown: isNaN(countDown) ? 0 : Math.max(0, Math.floor(countDown)),
                [k.BEDROCK_IDEA_CONTINUOUS_ENABLED]: config.enabled,
                [k.BEDROCK_IDEA_CONTINUOUS_CRON]: config.cron,
                [k.BEDROCK_IDEA_CONTINUOUS_COUNT_DOWN]: isNaN(countDown) ? 0 : Math.max(0, Math.floor(countDown)),
                ...(config.roundRobin !== undefined && {
                    roundRobin: config.roundRobin,
                    [k.BEDROCK_IDEA_CONTINUOUS_ROUND_ROBIN]: config.roundRobin
                })
            };
            const response = await ApiService.putData("/v1/bedrock-idea-round-robin/scheduler", payload);
            return {
                success: true,
                data: response
            };
        } catch (error) {
            Logger.error("Error updating round robin scheduler config:", error);
            return {
                success: false,
                error: error.message || "Failed to update scheduler config"
            };
        }
    }
};

export default BedrockIdeaService;
